import { useCallback, useEffect, useState } from 'react';

interface UseUnsavedChangesOptions {
  hasUnsavedChanges: boolean;
  onSave?: () => Promise<void> | void;
}

export const useUnsavedChanges = ({ hasUnsavedChanges, onSave }: UseUnsavedChangesOptions) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  // Handle browser refresh/close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const confirmNavigation = useCallback(() => {
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
    setShowConfirmDialog(false);
  }, [pendingNavigation]);

  const cancelNavigation = useCallback(() => {
    setPendingNavigation(null);
    setShowConfirmDialog(false);
  }, []);

  const saveAndNavigate = useCallback(async () => {
    if (onSave) {
      try {
        await onSave();
        confirmNavigation();
      } catch (error) {
        console.error('Failed to save:', error);
        // Still allow navigation on save error
        confirmNavigation();
      }
    } else {
      confirmNavigation();
    }
  }, [onSave, confirmNavigation]);

  const navigate = useCallback((navigationFn: () => void) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => navigationFn);
      setShowConfirmDialog(true);
    } else {
      navigationFn();
    }
  }, [hasUnsavedChanges]);

  return {
    showConfirmDialog,
    navigate,
    confirmNavigation,
    cancelNavigation,
    saveAndNavigate,
  };
};